import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProProfile, updateProProfile } from "../api";

interface ServiceItem {
  name: string;
  active: boolean;
  price: string;
  unit: string;
  description: string;
}

interface QuoteTemplate {
  id: string;
  name: string;
  service: string;
  intro: string;
  scope: string;
  deposit: string;
  timeline: string;
  warranty: string;
  payment: string;
  closing: string;
}

function newTemplate(service: string = "All Services"): QuoteTemplate {
  return {
    id: "tpl_" + Date.now(),
    name: service + " Quote",
    service,
    intro: "Thank you for your interest! Heres a detailed quote for your project:",
    scope: "Detailed scope of work for this service",
    deposit: "50% deposit required to begin work",
    timeline: "Typical completion: 1-3 days",
    warranty: "All work backed by 1-year warranty",
    payment: "Cash, check, Venmo, or card accepted",
    closing: "Please reach out with any questions!"
  };
}

export default function ServicesManagerPage() {
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"services" | "templates">("services");
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [editingTplId, setEditingTplId] = useState<string | null>(null);
  const [previewTplId, setPreviewTplId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const p = await getProProfile();
        setProfile(p);

        const stored = p.servicePricing || {};
        const subcats = p.subcategories || [];
        const paused = p.pausedSubcategories || [];

        const allSubs = Array.from(new Set([...subcats, ...paused]));
        const items: ServiceItem[] = allSubs.map((sub: string) => ({
          name: sub,
          active: subcats.includes(sub),
          price: stored[sub]?.price || String(p.hourlyRate || ""),
          unit: stored[sub]?.unit || "hour",
          description: stored[sub]?.description || "",
        }));
        setServices(items);

        const stored_tpls = p.quoteTemplates || [];
        if (stored_tpls.length > 0) setTemplates(stored_tpls);
        else if (p.quoteTemplate) {
          // Migrate old single template
          setTemplates([{ ...newTemplate(), ...p.quoteTemplate, name: "Default Quote" }]);
        } else {
          setTemplates([newTemplate()]);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function updateService(idx: number, key: keyof ServiceItem, value: any) {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  }

  function addTemplate(service: string) {
    const tpl = newTemplate(service);
    setTemplates(prev => [...prev, tpl]);
    setEditingTplId(tpl.id);
  }

  function updateTemplate(id: string, key: keyof QuoteTemplate, value: any) {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, [key]: value } : t));
  }

  function deleteTemplate(id: string) {
    if (!window.confirm("Delete this template?")) return;
    setTemplates(prev => prev.filter(t => t.id !== id));
  }

  async function saveAll() {
    setSaving(true);
    try {
      const servicePricing: any = {};
      services.forEach(s => {
        servicePricing[s.name] = { price: s.price, unit: s.unit, description: s.description };
      });
      const subcategories = services.filter(s => s.active).map(s => s.name);
      const pausedSubcategories = services.filter(s => !s.active).map(s => s.name);

      await updateProProfile({
        ...profile,
        subcategories,
        pausedSubcategories,
        servicePricing,
        quoteTemplates: templates,
      });
      alert("Saved!");
    } catch (e) {
      console.error(e);
      alert("Failed to save");
    } finally { setSaving(false); }
  }

  function generateQuote(tpl: QuoteTemplate) {
    const matchingService = services.find(s => s.name === tpl.service && s.active);
    const allActive = services.filter(s => s.active);

    let pricingSection: string[] = [];
    if (tpl.service === "All Services") {
      pricingSection = ["SERVICES & PRICING:", ...allActive.map(s => "• " + s.name + ": $" + s.price + "/" + s.unit + (s.description ? " — " + s.description : ""))];
    } else if (matchingService) {
      pricingSection = ["SERVICE:", "• " + matchingService.name + ": $" + matchingService.price + "/" + matchingService.unit];
    }

    return [
      tpl.intro,
      "",
      ...pricingSection,
      "",
      tpl.scope ? "SCOPE OF WORK:" : "",
      tpl.scope ? tpl.scope : "",
      tpl.scope ? "" : "",
      "TERMS:",
      "• " + tpl.deposit,
      "• " + tpl.timeline,
      "• " + tpl.warranty,
      "• " + tpl.payment,
      "",
      tpl.closing,
      "",
      "— " + (profile?.businessName || profile?.name || "Service Provider"),
    ].filter(l => l !== "" || true).join("\n");
  }

  function copyQuote(tpl: QuoteTemplate) {
    navigator.clipboard.writeText(generateQuote(tpl));
    alert("Quote copied to clipboard!");
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-5 pb-0 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigate("/home")} className="text-gray-400 text-sm font-semibold">← Dashboard</button>
          <button onClick={saveAll} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>
        <h1 className="font-display text-2xl font-bold text-navy mb-1">My Services</h1>
        <p className="text-sm text-gray-400 mb-3">Manage services, prices, and quote templates</p>

        <div className="flex gap-1 -mx-5 px-5">
          {[["services", "Services & Pricing"], ["templates", "Quote Templates (" + templates.length + ")"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key as any)}
              className={"py-3 px-4 text-xs font-bold transition-all relative " + (activeTab === key ? "text-mouna-blue" : "text-gray-400")}>
              {label}
              {activeTab === key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-mouna-blue rounded-full"/>}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {activeTab === "services" && (
          <>
            {services.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-card">
                <div className="text-4xl mb-3">📋</div>
                <h2 className="font-bold text-navy mb-2">No services yet</h2>
                <p className="text-sm text-gray-400 mb-4">Add subcategories from your profile edit page first.</p>
                <button onClick={() => navigate("/pro/edit")}
                  className="bg-mouna-blue text-white rounded-full px-6 py-2.5 text-sm font-bold">
                  Edit Profile →
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {services.map((svc, idx) => (
                  <div key={svc.name} className={"bg-white rounded-2xl p-4 shadow-card border-2 transition-all " +
                    (svc.active ? "border-teal" : "border-gray-100 opacity-60")}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-navy">{svc.name}</h3>
                          {svc.active ? (<span className="bg-teal-light text-teal text-xs font-bold rounded-full px-2 py-0.5">● Active</span>) : (<span className="bg-gray-100 text-gray-500 text-xs font-bold rounded-full px-2 py-0.5">⏸ Paused</span>)}
                        </div>
                        <div className="text-sm text-gray-600 font-semibold">${svc.price}/{svc.unit}</div>
                        {svc.description && <div className="text-xs text-gray-400 mt-1">{svc.description}</div>}
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => setEditingIndex(editingIndex === idx ? null : idx)}
                          className="bg-blue-pale text-mouna-blue text-xs font-bold rounded-full px-3 py-1.5">
                          {editingIndex === idx ? "Close" : "Edit"}
                        </button>
                        <button onClick={() => updateService(idx, "active", !svc.active)}
                          className={"text-xs font-bold rounded-full px-3 py-1.5 " + (svc.active ? "bg-yellow-50 text-yellow-600" : "bg-teal-light text-teal")}>
                          {svc.active ? "⏸ Pause" : "▶ Activate"}
                        </button>
                      </div>
                    </div>

                    {editingIndex === idx && (
                      <div className="border-t border-gray-100 pt-3 space-y-3 animate-fade-in">
                        <div>
                          <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">Price</label>
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                              <input type="number" value={svc.price} onChange={e => updateService(idx, "price", e.target.value)}
                                className="w-full border border-gray-200 rounded-xl pl-7 pr-3 py-2 text-sm outline-none focus:border-mouna-blue"/>
                            </div>
                            <select value={svc.unit} onChange={e => updateService(idx, "unit", e.target.value)}
                              className="border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue">
                              <option value="hour">per hour</option>
                              <option value="visit">per visit</option>
                              <option value="job">per job</option>
                              <option value="sq ft">per sq ft</option>
                              <option value="day">per day</option>
                              <option value="project">per project</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">Description (optional)</label>
                          <input type="text" value={svc.description} onChange={e => updateService(idx, "description", e.target.value)}
                            placeholder="e.g. Includes labor, materials extra"
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue"/>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "templates" && (
          <div className="space-y-3">
            <div className="bg-blue-pale rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-bold text-navy text-sm">Quote Templates</div>
                  <div className="text-xs text-gray-500">Create different quotes for different services</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => addTemplate("All Services")}
                  className="bg-mouna-blue text-white rounded-full px-4 py-2 text-xs font-bold">+ All Services</button>
                {services.filter(s => s.active).map(s => (
                  <button key={s.name} onClick={() => addTemplate(s.name)}
                    className="bg-white text-mouna-blue rounded-full px-4 py-2 text-xs font-bold border border-mouna-blue">+ {s.name}</button>
                ))}
              </div>
            </div>

            {templates.length === 0 && (
              <div className="bg-white rounded-2xl p-8 text-center">
                <div className="text-3xl mb-2">📝</div>
                <div className="text-sm text-gray-400">No templates yet. Add one above.</div>
              </div>
            )}

            {templates.map(tpl => (
              <div key={tpl.id} className="bg-white rounded-2xl p-4 shadow-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    {editingTplId === tpl.id ? (
                      <input type="text" value={tpl.name} onChange={e => updateTemplate(tpl.id, "name", e.target.value)}
                        className="w-full border-b border-gray-200 pb-1 font-bold text-navy outline-none focus:border-mouna-blue"/>
                    ) : (
                      <h3 className="font-bold text-navy">{tpl.name}</h3>
                    )}
                    <div className="text-xs text-mouna-blue mt-1">For: {tpl.service}</div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => setEditingTplId(editingTplId === tpl.id ? null : tpl.id)}
                      className="bg-blue-pale text-mouna-blue text-xs font-bold rounded-full px-3 py-1.5">
                      {editingTplId === tpl.id ? "Done" : "Edit"}
                    </button>
                    <button onClick={() => setPreviewTplId(previewTplId === tpl.id ? null : tpl.id)}
                      className="bg-gray-50 text-gray-600 text-xs font-bold rounded-full px-3 py-1.5">
                      {previewTplId === tpl.id ? "Hide" : "Preview"}
                    </button>
                    <button onClick={() => deleteTemplate(tpl.id)}
                      className="bg-red-50 text-red-500 text-xs font-bold rounded-full px-2 py-1.5">✕</button>
                  </div>
                </div>

                {editingTplId === tpl.id && (
                  <div className="space-y-2.5 mb-3 mt-3 border-t border-gray-100 pt-3">
                    <div>
                      <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">Service</label>
                      <select value={tpl.service} onChange={e => updateTemplate(tpl.id, "service", e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue">
                        <option value="All Services">All Services</option>
                        {services.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    {[
                      { key: "intro", label: "Intro / Greeting", multiline: false },
                      { key: "scope", label: "Scope of Work (optional)", multiline: true },
                      { key: "deposit", label: "Deposit Terms", multiline: false },
                      { key: "timeline", label: "Timeline", multiline: false },
                      { key: "warranty", label: "Warranty", multiline: false },
                      { key: "payment", label: "Payment Methods", multiline: false },
                      { key: "closing", label: "Closing", multiline: false },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1 block">{field.label}</label>
                        {field.multiline ? (
                          <textarea value={(tpl as any)[field.key]} onChange={e => updateTemplate(tpl.id, field.key as any, e.target.value)} rows={3}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue resize-none"/>
                        ) : (
                          <input type="text" value={(tpl as any)[field.key]} onChange={e => updateTemplate(tpl.id, field.key as any, e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue"/>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {previewTplId === tpl.id && (
                  <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-600 whitespace-pre-line font-mono leading-relaxed max-h-72 overflow-y-auto mb-2">
                    {generateQuote(tpl)}
                  </div>
                )}

                <button onClick={() => copyQuote(tpl)}
                  className="w-full bg-mouna-blue text-white rounded-full py-2.5 text-sm font-bold mt-2">
                  📋 Copy Quote
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
